/**
 * ToggleSwitch — on/off switch used across the settings page. The thumb
 * springs across (Oct 2026 motion pass), the track cross-fades, and the whole
 * control dips on press. Keyboard: it is a switch with a focus ring.
 */

import React, { useRef, useEffect } from 'react';
import { Animated } from 'react-native';
import { styles } from '../screens/profileStyles';
import PressableScale from './motion/PressableScale';
import { spring, timing } from '../utils/motion';

export default function ToggleSwitch({ isOn, onToggle, accessibilityLabel }) {
    const thumbPosition = useRef(new Animated.Value(isOn ? 1 : 0)).current;
    const switchBackground = useRef(new Animated.Value(isOn ? 1 : 0)).current;

    useEffect(() => {
        Animated.parallel([
            spring(thumbPosition, isOn ? 1 : 0, 'snappy', { useNative: false }),
            timing(switchBackground, isOn ? 1 : 0, { duration: 'fast', useNative: false }),
        ]).start();
    }, [isOn]);

    const thumbTranslateX = thumbPosition.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 36], // 72px width - 28px thumb width - 8px total margin = 36px movement
    });

    const backgroundColor = switchBackground.interpolate({
        inputRange: [0, 1],
        outputRange: ['rgba(0, 0, 0, 0)', 'rgba(255, 255, 255, 0.20)'],
    });

    return (
        <PressableScale
            style={[styles.toggleSwitch, isOn && styles.toggleSwitchOn]}
            onPress={onToggle}
            scaleTo={0.95}
            lift={0}
            accessibilityRole="switch"
            accessibilityState={{ checked: !!isOn }}
            accessibilityLabel={accessibilityLabel}
        >
            <Animated.View style={[styles.toggleSwitchBackground, { backgroundColor }]} />
            <Animated.View
                style={[
                    styles.toggleThumb,
                    isOn && styles.toggleThumbOn,
                    { transform: [{ translateX: thumbTranslateX }] }
                ]}
            />
        </PressableScale>
    );
}
